import * as React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import Button from '@atlaskit/button/new';

import { useAppUpdatesContext } from '@app/app/src/AppContext';
import { useUrlFilterSync } from '../hooks';

export const messages = defineMessages({
  label: {
    id: 'appUpdates.filters.reset.label',
    defaultMessage: 'Reset',
    description: 'Label for the reset filters button',
  },
});

/**
 * Helper to check if a filter value has any active selections.
 */
const hasValue = (value: unknown[] | string | null): boolean =>
  value !== null && value.length > 0;

/**
 * Reset button component for app updates filters.
 * Only visible when at least one filter has a value (draft or committed).
 *
 * Behavior:
 * - If filters have been applied (committed filters are active), clears everything
 *   including URL params and triggers a data refetch.
 * - If filters are only changed in draft (not yet applied), just clears the draft
 *   selections without refetching data.
 */
export const ResetButton: React.FC = () => {
  const { formatMessage } = useIntl();
  const { state, dispatch } = useAppUpdatesContext();
  const { syncFiltersToUrl } = useUrlFilterSync();

  const { draftFilters, committedFilters } = state.updates;

  const hasDraftFilters =
    hasValue(draftFilters.selectedProducts) ||
    hasValue(draftFilters.selectedStatuses) ||
    hasValue(draftFilters.selectedChangeTypes) ||
    hasValue(draftFilters.searchQuery);

  const hasCommittedFilters =
    hasValue(committedFilters.selectedProducts) ||
    hasValue(committedFilters.selectedStatuses) ||
    hasValue(committedFilters.selectedChangeTypes) ||
    hasValue(committedFilters.searchQuery);

  const handleClick = React.useCallback(() => {
    if (hasCommittedFilters) {
      // Filters were applied — full reset: clear URL, state, and refetch
      syncFiltersToUrl({
        selectedProducts: null,
        selectedStatuses: null,
        selectedChangeTypes: null,
        searchQuery: null,
        cursor: undefined,
      });

      dispatch({ type: 'UPDATES_FILTERS_RESET' });
    } else {
      // Filters only changed in draft — just clear draft selections, no refetch
      dispatch({ type: 'UPDATES_DRAFT_FILTERS_RESET' });
    }
  }, [dispatch, syncFiltersToUrl, hasCommittedFilters]);

  if (!hasDraftFilters && !hasCommittedFilters) {
    return null;
  }

  return (
    <Button
      testId="app-updates-reset-filter-button"
      onClick={handleClick}
      appearance="subtle"
    >
      {formatMessage(messages.label)}
    </Button>
  );
};
